import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/index.css";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LangProvider } from "@/contexts/LangContext";
import { AgeGateProvider } from "@/contexts/AgeGateContext";
import Layout from "@/components/Layout";
import AgeGate from "@/components/AgeGate";

import Home from "@/pages/Home";
import Directory from "@/pages/Directory";
import ProfileDetails from "@/pages/ProfileDetails";
import AgencyDetails from "@/pages/AgencyDetails";
import AgenciesList from "@/pages/AgenciesList";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import ClientDashboard from "@/pages/ClientDashboard";
import ProfileDashboard from "@/pages/ProfileDashboard";
import Wallet from "@/pages/Wallet";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminHome from "@/pages/admin/AdminHome";
import AdminProfiles from "@/pages/admin/AdminProfiles";
import AdminVerifications from "@/pages/admin/AdminVerifications";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminReports from "@/pages/admin/AdminReports";
import AdminManage from "@/pages/admin/AdminManage";
import AdminAuditLogs from "@/pages/admin/AdminAuditLogs";
import AdminChangePassword from "@/pages/admin/AdminChangePassword";
import Verification from "@/pages/Verification";
import Support from "@/pages/Support";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#737373]">Loading…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const loc = useLocation();
  // Synchronously handle Google OAuth callback before any route
  if (loc.hash?.includes("session_id=")) {
    return (
      <Layout>
        <AgeGate />
        <AuthCallback />
      </Layout>
    );
  }

  // Admin panel — separate from the public site: no age gate, no header/footer.
  if (loc.pathname.startsWith("/admin")) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard"     element={<AdminHome />} />
          <Route path="profiles"      element={<AdminProfiles />} />
          <Route path="verifications" element={<AdminVerifications />} />
          <Route path="users"         element={<AdminUsers />} />
          <Route path="reports"       element={<AdminReports />} />
          <Route path="manage"        element={<AdminManage />} />
          <Route path="audit-logs"    element={<AdminAuditLogs />} />
          <Route path="change-password" element={<AdminChangePassword />} />
        </Route>
        <Route path="/admin/*" element={<Navigate to="/admin/dashboard" replace />} />
      </Routes>
    );
  }

  return (
    <Layout>
      <AgeGate />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/directory" element={<Directory />} />
        <Route path="/profile/:id" element={<ProfileDetails />} />
        <Route path="/agencies" element={<AgenciesList />} />
        <Route path="/agency/:id" element={<AgencyDetails />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/support" element={<Support />} />
        <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
        <Route path="/verification" element={<Protected roles={["independent", "agency", "admin"]}><Verification /></Protected>} />
        <Route path="/dashboard/client" element={<Protected roles={["client", "admin"]}><ClientDashboard /></Protected>} />
        <Route path="/dashboard/independent" element={<Protected roles={["independent", "admin"]}><ProfileDashboard role="independent" /></Protected>} />
        <Route path="/dashboard/agency" element={<Protected roles={["agency", "admin"]}><ProfileDashboard role="agency" /></Protected>} />
        <Route path="/dashboard/admin" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <LangProvider>
        <AgeGateProvider>
          <AuthProvider>
            <AppRouter />
            <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#121212", border: "1px solid rgba(255,255,255,0.1)", color: "#f5f5f5" } }} />
          </AuthProvider>
        </AgeGateProvider>
      </LangProvider>
    </BrowserRouter>
  );
}
